import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { formatTime, isoDate } from "../lib/time";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import PhoneInput from "./PhoneInput";

function todayStr() {
  return isoDate(new Date());
}

function fmtTime(iso) {
  return formatTime(iso);
}

export default function BookingModal({
  open,
  onOpenChange,
  onCreated,
  stylists,
  services,
  defaults,
}) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState(null);
  const [searchedPhone, setSearchedPhone] = useState("");
  const [serviceId, setServiceId] = useState(defaults?.serviceId || "");
  const [stylistId, setStylistId] = useState(defaults?.stylistId || "");
  const [date, setDate] = useState(defaults?.date || todayStr());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [notes, setNotes] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerNotes, setCustomerNotes] = useState([]);

  useEffect(() => {
    if (!open) {
      // reset on close
      setPhone(""); setName(""); setCustomerId(null); setSearchedPhone("");
      setServiceId(defaults?.serviceId || "");
      setStylistId(defaults?.stylistId || "");
      setDate(defaults?.date || todayStr());
      setSlots([]); setSelectedSlot(""); setNotes("");
      setCustomerNotes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const eligibleStylists = useMemo(() => {
    const svc = services.find((s) => s.id === serviceId);
    if (!svc || !svc.stylist_ids?.length) return stylists.filter((s) => s.active);
    return stylists.filter((s) => s.active && svc.stylist_ids.includes(s.id));
  }, [services, stylists, serviceId]);

  useEffect(() => {
    if (stylistId && !eligibleStylists.find((s) => s.id === stylistId)) {
      setStylistId("");
      setSlots([]);
      setSelectedSlot("");
    }
  }, [eligibleStylists, stylistId]);

  const lookupCustomer = async () => {
    if (!phone) return;
    try {
      const { data } = await api.get(`/customers/by-phone/${encodeURIComponent(phone)}`);
      setSearchedPhone(phone);
      if (data) {
        setCustomerId(data.id);
        setName(data.name);
        toast.success(`Found ${data.name}`);
        // Pull operational-memory notes so the receptionist sees allergies / formulas / preferences.
        try {
          const { data: notes } = await api.get(`/customers/${data.id}/notes`);
          setCustomerNotes(notes.slice(0, 3));
        } catch (_e) { /* notes are best-effort */ }
      } else {
        setCustomerId(null);
        setCustomerNotes([]);
        toast.info("New customer — enter a name to create");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const fetchSlots = async () => {
    if (!serviceId || !stylistId || !date) return;
    setLoadingSlots(true);
    setSelectedSlot("");
    try {
      const { data } = await api.get("/availability", {
        params: { service_id: serviceId, stylist_id: stylistId, date },
      });
      setSlots(data.slots || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (serviceId && stylistId && date) fetchSlots();
    else setSlots([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, stylistId, date]);

  const ensureCustomer = async () => {
    if (customerId) return customerId;
    if (!phone || !name) {
      toast.error("Please enter customer phone and name");
      return null;
    }
    try {
      const { data } = await api.post("/customers", { phone, name });
      setCustomerId(data.id);
      return data.id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      return null;
    }
  };

  const submit = async () => {
    if (!serviceId || !stylistId || !selectedSlot) {
      toast.error("Select service, stylist and a time slot");
      return;
    }
    setSubmitting(true);
    const cid = await ensureCustomer();
    if (!cid) { setSubmitting(false); return; }
    try {
      const { data } = await api.post("/bookings", {
        customer_id: cid,
        stylist_id: stylistId,
        service_id: serviceId,
        start_at: selectedSlot,
        notes: notes || undefined,
      });
      toast.success("Booking created");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white border-border" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">New booking</DialogTitle>
          <DialogDescription>Find or create a customer, then pick a time slot.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {/* Customer */}
          <div className="space-y-2">
            <Label className="overline">Customer phone</Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <PhoneInput
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  data-testid="booking-phone-input"
                  className="bg-[#F9F8F6]"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={lookupCustomer}
                data-testid="booking-find-customer-btn"
              >
                <MagnifyingGlass size={16} className="mr-1" /> Find
              </Button>
            </div>
            {searchedPhone && (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={customerId ? "Customer found" : "New customer name"}
                data-testid="booking-name-input"
                className="bg-[#F9F8F6]"
                disabled={!!customerId}
              />
            )}
            {customerId && customerNotes.length > 0 && (
              <div className="rounded-md border border-[#E0DBD0] bg-[#F4F1EA] px-3 py-2.5 space-y-1.5" data-testid="booking-customer-notes">
                <div className="overline">Customer notes</div>
                {customerNotes.map((n) => (
                  <div key={n.id} className="text-xs text-foreground leading-relaxed" data-testid={`booking-customer-note-${n.id}`}>
                    · {n.body}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="overline">Service</Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger data-testid="booking-service-select" className="bg-[#F9F8F6]">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter((s) => s.active).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} · {s.duration_minutes}m
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline">Stylist</Label>
              <Select value={stylistId} onValueChange={setStylistId} disabled={!serviceId}>
                <SelectTrigger data-testid="booking-stylist-select" className="bg-[#F9F8F6]">
                  <SelectValue placeholder="Select stylist" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleStylists.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="overline">Date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="booking-date-input"
              className="bg-[#F9F8F6]"
            />
          </div>

          <div className="space-y-2">
            <Label className="overline">Available slots</Label>
            <div
              className="max-h-44 overflow-auto scrollbar-thin border border-border rounded-md p-2 bg-[#F9F8F6]"
              data-testid="booking-slots"
            >
              {loadingSlots && <div className="text-sm text-muted-foreground p-3">Loading slots...</div>}
              {!loadingSlots && slots.length === 0 && (
                <div className="text-sm text-muted-foreground p-3">
                  {serviceId && stylistId
                    ? "No availability on this day."
                    : "Select a service, stylist, and date."}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button
                    key={s.start}
                    type="button"
                    onClick={() => setSelectedSlot(s.start)}
                    data-testid={`slot-${s.start}`}
                    className={`text-sm rounded-md px-2 py-2 border transition-all duration-200 ${
                      selectedSlot === s.start
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-white border-border hover:border-primary"
                    }`}
                  >
                    {fmtTime(s.start)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="overline">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
              data-testid="booking-notes-input"
              className="bg-[#F9F8F6] min-h-[64px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="booking-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="booking-submit-btn"
            className="bg-primary hover:bg-[#2B4F39]"
          >
            {submitting ? "Creating..." : "Create booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
